import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  XMarkIcon,
  DocumentTextIcon,
  FolderIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { GoogleDriveAttachment } from "../../types";
import { apiService } from "../../services/apiService";
import toast from "../../utils/toast";

interface GoogleDriveFilePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (files: GoogleDriveAttachment[]) => void;
}

export const GoogleDriveFilePicker: React.FC<GoogleDriveFilePickerProps> = ({
  isOpen,
  onClose,
  onSelect,
}) => {
  const [files, setFiles] = useState<GoogleDriveAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentFolder, setCurrentFolder] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [folderPath, setFolderPath] = useState<
    Array<{ id: string | null; name: string }>
  >([{ id: null, name: "Ana Klasör" }]);

  useEffect(() => {
    if (isOpen) {
      loadFiles();
    } else {
      // Reset state when closing
      setSelectedFiles(new Set());
      setSearchTerm("");
      setCurrentFolder(null);
      setFolderPath([{ id: null, name: "Ana Klasör" }]);
    }
  }, [isOpen, currentFolder]);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const response = await apiService.listGoogleDriveFiles(
        currentFolder || undefined
      );
      setFiles(response);
    } catch (error) {
      toast.error(
        "Dosyalar yüklenemedi. Google Drive bağlantısını kontrol edin."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFileClick = (file: GoogleDriveAttachment) => {
    // If it's a folder, navigate into it
    if (file.mimeType === "application/vnd.google-apps.folder") {
      setSearchTerm("");
      setCurrentFolder(file.id);
      setFolderPath([...folderPath, { id: file.id, name: file.name }]);
      return;
    }

    // Otherwise, toggle selection
    const newSet = new Set(selectedFiles);
    if (newSet.has(file.id)) {
      newSet.delete(file.id);
    } else {
      newSet.add(file.id);
    }
    setSelectedFiles(newSet);
  };

  const handleBreadcrumbClick = (index: number) => {
    const folder = folderPath[index];
    setSearchTerm("");
    setCurrentFolder(folder.id);
    setFolderPath(folderPath.slice(0, index + 1));
  };

  const handleSelect = async () => {
    if (selectedFiles.size === 0) {
      toast.error("Lütfen en az bir dosya seçin");
      return;
    }

    setLoading(true);
    try {
      const attachments = await Promise.all(
        Array.from(selectedFiles).map(async (fileId) => {
          const attachment = await apiService.attachGoogleDriveFile(fileId);
          return attachment;
        })
      );
      onSelect(attachments);
      toast.success(`${attachments.length} dosya eklendi`);
      onClose();
    } catch (error) {
      toast.error("Dosyalar eklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const filteredFiles = files.filter((file) =>
    file.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getFileIcon = (file: GoogleDriveAttachment) => {
    if (file.mimeType === "application/vnd.google-apps.folder") {
      return <FolderIcon className="w-full h-32 text-blue-400" />;
    }
    if (file.thumbnailUrl) {
      return (
        <img
          src={file.thumbnailUrl}
          alt={file.name}
          className="w-full h-32 object-cover rounded"
        />
      );
    }
    if (file.iconUrl) {
      return (
        <img
          src={file.iconUrl}
          alt={file.name}
          className="w-16 h-16 mx-auto mt-8"
        />
      );
    }
    return <DocumentTextIcon className="w-full h-32 text-gray-400" />;
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[220] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[85vh] min-h-0 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-gray-900">
              Google Drive Dosyaları
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Breadcrumb Navigation */}
          <div className="flex items-center space-x-2 text-sm mb-4">
            {folderPath.map((folder, index) => (
              <React.Fragment key={folder.id || `root-${index}`}>
                {index > 0 && <span className="text-gray-400">/</span>}
                <button
                  onClick={() => handleBreadcrumbClick(index)}
                  className={`hover:text-blue-600 transition-colors ${
                    index === folderPath.length - 1
                      ? "text-blue-600 font-semibold"
                      : "text-gray-600"
                  }`}
                >
                  {folder.name}
                </button>
              </React.Fragment>
            ))}
          </div>

          {/* Search Bar */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Dosya ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* File List */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-500">Dosyalar yükleniyor...</p>
              </div>
            </div>
          ) : filteredFiles.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <DocumentTextIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg">
                {searchTerm
                  ? "Arama sonucu bulunamadı"
                  : "Bu klasörde dosya yok"}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {filteredFiles.map((file) => {
                const isFolder =
                  file.mimeType === "application/vnd.google-apps.folder";
                const isSelected = selectedFiles.has(file.id);

                return (
                  <div
                    key={file.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleFileClick(file)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleFileClick(file);
                      }
                    }}
                    className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all hover:shadow-md ${
                      isSelected
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-blue-300"
                    } ${isFolder ? "bg-blue-50/30" : ""}`}
                  >
                    {/* Selection Checkbox */}
                    {!isFolder && (
                      <div className="absolute top-2 right-2">
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            isSelected
                              ? "bg-blue-500 border-blue-500"
                              : "bg-white border-gray-300"
                          }`}
                        >
                          {isSelected && (
                            <svg
                              className="w-3 h-3 text-white"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path d="M5 13l4 4L19 7"></path>
                            </svg>
                          )}
                        </div>
                      </div>
                    )}

                    {/* File Icon/Thumbnail */}
                    <div className="mb-3">{getFileIcon(file)}</div>

                    {/* File Name */}
                    <p
                      className="text-sm font-medium text-gray-900 truncate"
                      title={file.name}
                    >
                      {file.name}
                    </p>

                    {/* Folder Indicator */}
                    {isFolder && (
                      <p className="text-xs text-blue-600 mt-1">Klasör</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {selectedFiles.size > 0 ? (
                <span className="font-semibold text-blue-600">
                  {selectedFiles.size} dosya seçildi
                </span>
              ) : (
                <span>Dosya seçmek için tıklayın</span>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                disabled={loading}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                İptal
              </button>
              <button
                onClick={handleSelect}
                disabled={selectedFiles.size === 0 || loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Ekleniyor..." : "Dosyaları Ekle"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document !== "undefined") {
    return createPortal(modalContent, document.body);
  }

  return modalContent;
};
